import api from "./api";
import { formatReportDateTime } from "./energyReportData";

const WEEKDAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const HEATMAP_COLOR = "#1ba08e";

function normalizeArray(value) {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") return Object.values(value);
  if (value == null) return [];
  return [value];
}

function parseNumericValue(value) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (value == null) return null;
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseTimestampRaw(rawLabel) {
  if (!rawLabel) return null;
  const rawStr = String(rawLabel);
  const match = rawStr.match(
    /(\d{4})[-/](\d{1,2})[-/](\d{1,2})[T\s](\d{1,2}):(\d{1,2})/,
  );
  if (!match) {
    const dateMatch = rawStr.match(/(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
    if (dateMatch) {
      const [, year, month, day] = dateMatch;
      return { year, month, day, hours: "00", minutes: "00" };
    }
    return null;
  }
  const [, year, month, day, hours, minutes] = match;
  return {
    year,
    month: month.padStart(2, "0"),
    day: day.padStart(2, "0"),
    hours: hours.padStart(2, "0"),
    minutes: minutes.padStart(2, "0"),
  };
}

function findMachineEntry(machines, targetName) {
  if (!machines || !targetName) return null;
  return (
    Object.values(machines).find((machine) => machine?.name === targetName) ||
    null
  );
}

export async function fetchHeatmapEnergyData(username, dateRange, scopeNodeName) {
  if (!username || !dateRange?.start || !dateRange?.end) {
    throw new Error("Missing username or date range.");
  }

  const isWholeFacility = !scopeNodeName || scopeNodeName === "Usage";
  const selectedMachines = isWholeFacility ? "Usage" : scopeNodeName;

  const response = await api.get("/machines_data", {
    params: {
      username,
      mode: "hour",
      start: formatReportDateTime(dateRange.start),
      end: formatReportDateTime(dateRange.end),
      selected_machines: selectedMachines,
      metric: "energy",
    },
  });

  const raw = response.data?.data || response.data;
  const timestamps = normalizeArray(raw?.["Date & Time"]);
  const machineEntry = findMachineEntry(
    raw?.machines,
    isWholeFacility ? "Usage" : scopeNodeName,
  );
  const values = normalizeArray(machineEntry?.data?.kWh).map(parseNumericValue);

  return processHeatmapRows(timestamps, values);
}

export function processHeatmapRows(timestamps, values) {
  if (!timestamps.length || !values.length) {
    return { rows: [], timeLabels: [], minValue: null, maxValue: null };
  }

  const weekdayBuckets = new Map();
  WEEKDAYS.forEach((day) => {
    weekdayBuckets.set(day, { label: day, values: {} });
  });

  timestamps.forEach((rawLabel, index) => {
    const parsed = parseTimestampRaw(rawLabel);
    if (!parsed) return;

    const { year, month, day, hours } = parsed;
    const date = new Date(year, parseInt(month, 10) - 1, parseInt(day, 10));
    let dayIdx = date.getDay();
    if (dayIdx === 0) dayIdx = 7;
    const dayName = WEEKDAYS[dayIdx - 1];
    const timeKey = `${hours}:00`;
    const value = values[index];

    if (value == null) return;

    const bucket = weekdayBuckets.get(dayName);
    if (!bucket.values[timeKey]) {
      bucket.values[timeKey] = { sum: 0, count: 0 };
    }
    bucket.values[timeKey].sum += value;
    bucket.values[timeKey].count += 1;
  });

  const timeLabels = Array.from(
    { length: 24 },
    (_, i) => `${String(i).padStart(2, "0")}:00`,
  );

  const rows = WEEKDAYS.map((dayName) => {
    const bucket = weekdayBuckets.get(dayName);
    return {
      label: dayName,
      values: timeLabels.map((timeKey) => {
        const stats = bucket.values[timeKey];
        return stats && stats.count > 0 ? stats.sum / stats.count : null;
      }),
    };
  });

  const allValues = rows
    .flatMap((row) => row.values)
    .filter((value) => value != null);

  return {
    rows,
    timeLabels,
    minValue: allValues.length ? Math.min(...allValues) : null,
    maxValue: allValues.length ? Math.max(...allValues) : null,
  };
}

function blendColor(value, minValue, maxValue) {
  if (value == null || Number.isNaN(value)) {
    return { bg: "#f8fafc", text: "#94a3b8" };
  }

  const spread = (maxValue - minValue) || 1;
  const intensity = Math.min(Math.max((value - minValue) / spread, 0), 1);
  const mapped = 0.12 + intensity * 0.88;

  const hex = HEATMAP_COLOR.replace("#", "");
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  const outR = Math.round(255 * (1 - mapped) + r * mapped);
  const outG = Math.round(255 * (1 - mapped) + g * mapped);
  const outB = Math.round(255 * (1 - mapped) + b * mapped);

  return {
    bg: `rgb(${outR},${outG},${outB})`,
    text: mapped > 0.55 ? "#ffffff" : "#334155",
  };
}

export function renderHeatmapChartImage(heatmapData) {
  const { rows, timeLabels, minValue, maxValue } = heatmapData || {};
  const canvas = document.createElement("canvas");
  canvas.width = 1000;
  canvas.height = 360;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  if (!rows?.length || !timeLabels?.length) {
    ctx.fillStyle = "#64748b";
    ctx.font = "16px Helvetica, Arial, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(
      "No heatmap data for the selected range.",
      canvas.width / 2,
      canvas.height / 2,
    );
    return canvas.toDataURL("image/png");
  }

  const margin = { top: 28, left: 88, right: 16, bottom: 24 };
  const gridWidth = canvas.width - margin.left - margin.right;
  const gridHeight = canvas.height - margin.top - margin.bottom;
  const cellW = gridWidth / timeLabels.length;
  const cellH = gridHeight / rows.length;

  ctx.fillStyle = "#475569";
  ctx.font = "bold 11px Helvetica, Arial, sans-serif";
  ctx.textAlign = "center";
  timeLabels.forEach((label, index) => {
    if (index % 2 !== 0) return;
    const x = margin.left + index * cellW + cellW / 2;
    ctx.fillText(label.slice(0, 5), x, margin.top - 8);
  });

  rows.forEach((row, rowIndex) => {
    const y = margin.top + rowIndex * cellH;
    ctx.fillStyle = "#334155";
    ctx.font = "10px Helvetica, Arial, sans-serif";
    ctx.textAlign = "right";
    ctx.fillText(row.label.slice(0, 3), margin.left - 8, y + cellH / 2 + 3);

    row.values.forEach((value, colIndex) => {
      const x = margin.left + colIndex * cellW;
      const { bg } = blendColor(value, minValue, maxValue);
      ctx.fillStyle = bg;
      ctx.fillRect(x + 1, y + 1, cellW - 2, cellH - 2);
    });
  });

  if (minValue != null && maxValue != null) {
    const legendY = canvas.height - 10;
    const legendX = margin.left;
    const legendW = 140;
    const grad = ctx.createLinearGradient(legendX, 0, legendX + legendW, 0);
    grad.addColorStop(0, blendColor(minValue, minValue, maxValue).bg);
    grad.addColorStop(1, blendColor(maxValue, minValue, maxValue).bg);
    ctx.fillStyle = grad;
    ctx.fillRect(legendX, legendY - 8, legendW, 8);
    ctx.fillStyle = "#64748b";
    ctx.font = "9px Helvetica, Arial, sans-serif";
    ctx.textAlign = "left";
    ctx.fillText(`${minValue.toFixed(1)} kWh`, legendX, legendY - 10);
    ctx.textAlign = "right";
    ctx.fillText(`${maxValue.toFixed(1)} kWh`, legendX + legendW, legendY - 10);
  }

  return canvas.toDataURL("image/png");
}
